import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Box, Typography, useTheme, IconButton, CircularProgress, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContactRow from "./ContactRow";
import DashboardRowSkeleton from "./DashboardRowSkeleton";
import { useContactSelection } from "../../../../hooks/useContactSelection";
import { setSelectedInboxTab } from "../../../../redux/Slice/Inbox/inboxSlice";
import { fetchInbox, fetchClosedTabInbox, loadMoreInbox } from "../../../../redux/Slice/Inbox/inboxRecentSlice";
import { fetchUserDetails } from "../../../../redux/Slice/User/userDetailsSlice";
import {
  selectInboxRecentData,
  selectInboxRecentPagination,
  selectInboxRecentLazyTabsLoaded,
  selectInboxRecentInitialized,
  selectUserDetailsData,
} from "../../../../redux/selectors";

const TABS = ["Assigned", "Unassigned", "Closed"];
const TAB_DESCRIPTIONS = {
  Assigned: "Contacts currently handled by a team member.",
  Unassigned: "New or open contacts nobody has picked up yet.",
  Closed: "Conversations that have been marked resolved and closed.",
};
// Constrains the tooltip into a compact wrapped box (rather than one long unwrapped
// strip) and anchors it directly under the tab — matches LeftNav.jsx's RAIL_TOOLTIP_SX
// dark-bubble convention.
const TAB_TOOLTIP_SX = {
  tooltip: {
    sx: {
      backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#E2E2E4" : "#1A1A1A"),
      color: (theme) => (theme.palette.mode === "dark" ? "#1A1A1A" : "#F5F5F5"),
      fontSize: "0.75rem",
      fontWeight: 500,
      borderRadius: "8px",
      maxWidth: 190,
      textAlign: "center",
      px: 1.25,
      py: 0.75,
      boxShadow: (theme) =>
        theme.palette.mode === "dark" ? "0 6px 16px rgba(0,0,0,0.35)" : "0 4px 12px rgba(0,0,0,0.15)",
    },
  },
  arrow: {
    sx: {
      color: (theme) => (theme.palette.mode === "dark" ? "#E2E2E4" : "#1A1A1A"),
    },
  },
};
// Per-request: this widget always shows 5 contacts at a time, independent of the
// 50-per-request batch size the real Inbox page's pagination is tuned for (see
// inboxRecentSlice.js's pagination.limit) — pages here are sliced client-side out of
// whatever's already loaded, only reaching for loadMoreInbox once that runs out.
const PAGE_SIZE = 5;

// ContactsModel's `status` is exactly Assigned | Unassigned | Closed (see ContactsModel.js).
// getinbox buckets Unassigned and Closed on their own, but has no standalone "Assigned"
// category — its "All" bucket is simply every non-Closed contact, i.e. Assigned + Unassigned
// combined (see inboxController.js's liteAll/liteUnassigned filters). So the Assigned tab is
// derived here by filtering "All" down to status === "Assigned", and reuses All's own
// pagination/loadMoreInbox to fetch further pages when it runs out.
const TAB_SOURCE_CATEGORY = { Assigned: "All", Unassigned: "Unassigned", Closed: "Closed" };

function ContactsPanel({ channelIcons, statusColor }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid } = useParams();
  const { selectContact } = useContactSelection();

  const userDetails = useSelector(selectUserDetailsData);
  const inboxData = useSelector(selectInboxRecentData);
  const inboxPagination = useSelector(selectInboxRecentPagination);
  const lazyTabsLoaded = useSelector(selectInboxRecentLazyTabsLoaded);
  const initialized = useSelector(selectInboxRecentInitialized);

  const [activeTab, setActiveTab] = useState("Assigned");
  const [page, setPage] = useState(1);
  const [pageDirection, setPageDirection] = useState(1); // 1 = forward (next), -1 = backward (prev) — drives the slide direction below
  const [loadingMorePage, setLoadingMorePage] = useState(false);

  useEffect(() => {
    dispatch(fetchInbox());
  }, [dispatch]);

  useEffect(() => {
    if (!userDetails) dispatch(fetchUserDetails());
  }, [dispatch, userDetails]);

  // Closed contacts are excluded from the initial fetchInbox response (see
  // inboxController.js's skipClosed handling) — lazily loaded on first visit to the tab,
  // same as the real Inbox's Closed tab (InboxAll.jsx).
  useEffect(() => {
    if (activeTab === "Closed" && !lazyTabsLoaded.Closed) {
      dispatch(fetchClosedTabInbox("Closed"));
    }
  }, [activeTab, lazyTabsLoaded.Closed, dispatch]);

  const sourceCategory = TAB_SOURCE_CATEGORY[activeTab];
  const sourceList = inboxData[sourceCategory] || [];
  const contacts = activeTab === "Assigned"
    ? sourceList.filter((c) => c.status === "Assigned")
    : sourceList;
  const sourcePagination = inboxPagination[sourceCategory] || { hasMore: false, totalCount: sourceList.length };
  // Assigned's exact total is All's total minus Unassigned's total (both server-accurate) —
  // no separate count comes back from getinbox for "Assigned" alone.
  const totalCount = activeTab === "Assigned"
    ? Math.max(0, (sourcePagination.totalCount ?? sourceList.length) - (inboxPagination.Unassigned?.totalCount ?? 0))
    : (sourcePagination.totalCount ?? sourceList.length);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const visibleContacts = contacts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleTabClick = (tab) => {
    if (tab === activeTab) return;
    setPageDirection(0); // 0 = tab switch (vertical fade below) — distinct from pager's horizontal slide
    setActiveTab(tab);
    setPage(1);
  };

  const handlePrev = () => {
    setPageDirection(-1);
    setPage((p) => Math.max(1, p - 1));
  };

  // Advances to the next page of 5. If those rows aren't in the already-loaded batch yet
  // (loadMoreInbox's 50-per-request page hasn't reached them), fetch the next batch first —
  // mirrors InboxAll.jsx's "load more" flow, just triggered by this panel's own pager.
  const handleNext = useCallback(() => {
    setPageDirection(1);
    const nextPage = page + 1;
    const neededCount = nextPage * PAGE_SIZE;
    // For Assigned, totalCount is the exact server-side figure (see above), so "more to load"
    // means more loaded+filtered rows than we currently have — not sourcePagination.hasMore,
    // which reflects All's own (Assigned+Unassigned) remaining rows, not Assigned's alone.
    const hasMoreToLoad = activeTab === "Assigned"
      ? contacts.length < totalCount
      : sourcePagination.hasMore;
    if (contacts.length >= neededCount || !hasMoreToLoad) {
      setPage(nextPage);
      return;
    }
    setLoadingMorePage(true);
    dispatch(loadMoreInbox(sourceCategory))
      .unwrap()
      .then(() => setPage(nextPage))
      .catch(() => {})
      .finally(() => setLoadingMorePage(false));
  }, [page, contacts.length, totalCount, activeTab, sourcePagination.hasMore, dispatch, sourceCategory]);

  const handleContactClick = useCallback((contactId) => {
    const effectiveUserid = userDetails?._id;
    if (!accountid || !effectiveUserid) return;
    // A contact clicked from this panel's Closed tab is known to be Closed: hint the Inbox so it
    // opens under its Closed tab rather than correcting from "All" afterwards.
    // Inbox isn't mounted here, so setting its tab first can't trigger any tab-change effect.
    if (activeTab === "Closed") dispatch(setSelectedInboxTab("Closed"));
    selectContact(contactId, {
      navigate: true,
      accountid,
      userid: effectiveUserid,
      navigateState: activeTab === "Closed" ? { inboxTab: "Closed" } : null,
    });
  }, [selectContact, accountid, userDetails, activeTab, dispatch]);

  const handleViewAllMessages = useCallback(() => {
    const effectiveUserid = userDetails?._id;
    if (!accountid || !effectiveUserid) return;
    navigate(`/home/${accountid}/inbox/${effectiveUserid}`);
  }, [navigate, accountid, userDetails]);

  // `initialized` (not `loading`) gates the skeleton: inboxRecentSlice.js's `loading` flag
  // gets prematurely reset to false by its own internal debounce-reject (a pre-existing bug,
  // triggered locally by React.StrictMode's double-mount-effect — see current-work.md) even
  // while a real fetch is still in flight. `initialized` doesn't have that problem — the
  // reducer only ever flips it true on a genuine fulfilled/real-error, never on a debounced
  // rejection — so gating on it keeps the skeleton up for the whole real fetch, immune to
  // that race, without touching the buggy reducer itself.
  const showInitialLoading = !initialized && contacts.length === 0;

  return (
    <Box sx={{ flex: 1, minHeight: 520, backgroundColor: theme.palette.background.default, border: `1px solid ${theme.palette.divider}`, borderRadius: "24px", p: 2, display: "flex", flexDirection: "column" }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>Contacts</Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 2, mb: 2.25, fontSize: "0.875rem" }}>
        {TABS.map((s) => (
          <Tooltip key={s} title={TAB_DESCRIPTIONS[s]} placement="bottom" arrow componentsProps={TAB_TOOLTIP_SX}>
            <Typography
              onClick={() => handleTabClick(s)}
              sx={{
                fontWeight: s === activeTab ? 600 : 400,
                color: s === activeTab ? "text.primary" : "text.secondary",
                borderBottom: s === activeTab ? "2px solid" : "none",
                borderColor: "primary.main",
                pb: 0.5,
                fontSize: "inherit",
                cursor: "pointer",
              }}
            >
              {s}
            </Typography>
          </Tooltip>
        ))}
      </Box>
      {showInitialLoading ? (
        <DashboardRowSkeleton count={PAGE_SIZE} />
      ) : visibleContacts.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            textAlign: "center",
            padding: 0,
          }}
        >
          <Box sx={{ mb: 2, color: (theme) => theme.palette.text.secondary, display: "flex" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="58" height="58" viewBox="0 0 24 24" fill="none">
              <g clipPath="url(#clip0_4418_7624)">
                <path d="M17 21.75H7C2.59 21.75 1.25 20.41 1.25 16V8C1.25 3.59 2.59 2.25 7 2.25H17C21.41 2.25 22.75 3.59 22.75 8V16C22.75 20.41 21.41 21.75 17 21.75ZM7 3.75C3.42 3.75 2.75 4.43 2.75 8V16C2.75 19.57 3.42 20.25 7 20.25H17C20.58 20.25 21.25 19.57 21.25 16V8C21.25 4.43 20.58 3.75 17 3.75H7Z" fill="currentColor" />
                <path d="M19 8.75H14C13.59 8.75 13.25 8.41 13.25 8C13.25 7.59 13.59 7.25 14 7.25H19C19.41 7.25 19.75 7.59 19.75 8C19.75 8.41 19.41 8.75 19 8.75Z" fill="currentColor" />
                <path d="M19 12.75H15C14.59 12.75 14.25 12.41 14.25 12C14.25 11.59 14.59 11.25 15 11.25H19C19.41 11.25 19.75 11.59 19.75 12C19.75 12.41 19.41 12.75 19 12.75Z" fill="currentColor" />
                <path d="M19 16.75H17C16.59 16.75 16.25 16.41 16.25 16C16.25 15.59 16.59 15.25 17 15.25H19C19.41 15.25 19.75 15.59 19.75 16C19.75 16.41 19.41 16.75 19 16.75Z" fill="currentColor" />
                <path d="M8.49994 12.0399C7.08994 12.0399 5.93994 10.8899 5.93994 9.47992C5.93994 8.06992 7.08994 6.91992 8.49994 6.91992C9.90994 6.91992 11.0599 8.06992 11.0599 9.47992C11.0599 10.8899 9.90994 12.0399 8.49994 12.0399ZM8.49994 8.41992C7.91994 8.41992 7.43994 8.89992 7.43994 9.47992C7.43994 10.0599 7.91994 10.5399 8.49994 10.5399C9.07994 10.5399 9.55994 10.0599 9.55994 9.47992C9.55994 8.89992 9.07994 8.41992 8.49994 8.41992Z" fill="currentColor" />
                <path d="M11.9999 17.0798C11.6199 17.0798 11.2899 16.7898 11.2499 16.3998C11.1399 15.3198 10.2699 14.4498 9.17991 14.3498C8.71991 14.3098 8.25991 14.3098 7.79991 14.3498C6.70991 14.4498 5.83991 15.3098 5.72991 16.3998C5.68991 16.8098 5.31991 17.1198 4.90991 17.0698C4.49991 17.0298 4.19991 16.6598 4.23991 16.2498C4.41991 14.4498 5.84991 13.0198 7.65991 12.8598C8.20991 12.8098 8.76991 12.8098 9.31991 12.8598C11.1199 13.0298 12.5599 14.4598 12.7399 16.2498C12.7799 16.6598 12.4799 17.0298 12.0699 17.0698C12.0499 17.0798 12.0199 17.0798 11.9999 17.0798Z" fill="currentColor" />
              </g>
              <defs>
                <clipPath id="clip0_4418_7624">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </Box>
          <Typography variant="h6" color="text.secondary">
            {`No ${activeTab.toLowerCase()} contacts`}
          </Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: 0.5 }}>
            {TAB_DESCRIPTIONS[activeTab]}
          </Typography>
        </Box>
      ) : (
        // No overflow:hidden here — it would clip ContactRow's own negative-margin
        // hover bleed (mx:-1.25, the breathing-room fix). The slide is only 16px and
        // brief (200ms), so leaving this unclipped is the right tradeoff over losing
        // the hover styling.
        <Box sx={{ position: "relative" }}>
          <AnimatePresence mode="wait" initial={false} custom={pageDirection}>
            <motion.div
              key={`${activeTab}-${page}`}
              custom={pageDirection}
              // dir 0 = tab switch → vertical fade (distinct from the pager's horizontal
              // slide below), signals "different content category" rather than "same list,
              // next/prev page".
              initial={(dir) => (dir === 0 ? { opacity: 0, y: 8 } : { opacity: 0, x: dir * 16 })}
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={(dir) => (dir === 0 ? { opacity: 0, y: -8 } : { opacity: 0, x: dir * -16 })}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {visibleContacts.map((row, idx) => (
                <ContactRow
                  key={row.contactId}
                  contact={row}
                  channelIcons={channelIcons}
                  statusColor={statusColor}
                  isLast={idx === visibleContacts.length - 1}
                  onClick={() => handleContactClick(row.contactId)}
                />
              ))}
            </motion.div>
          </AnimatePresence>
        </Box>
      )}
      <Box sx={{ mt: "auto", pt: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" disabled={page === 1} onClick={handlePrev} sx={{ width: 26, height: 26, border: `1px solid ${theme.palette.divider}` }}>
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "text.secondary" }}>
            {loadingMorePage ? <CircularProgress size={11} /> : `Page ${page} of ${totalPages}`}
          </Typography>
          <IconButton size="small" disabled={page >= totalPages || loadingMorePage} onClick={handleNext} sx={{ width: 26, height: 26, border: `1px solid ${theme.palette.divider}` }}>
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        <Typography onClick={handleViewAllMessages} sx={{ fontSize: "0.75rem", fontWeight: 600, color: "text.secondary", border: `1px solid ${theme.palette.divider}`, borderRadius: "8px", px: 1.25, py: 0.5, cursor: "pointer" }}>View All Messages</Typography>
      </Box>
    </Box>
  );
}

export default ContactsPanel;
